import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
  CicloFiltro,
  CicloInfo,
  CicloOpcaoResponse,
  CicloRequest,
  CicloResponse,
  CicloResumo,
  HistoricoCiclo,
  HistoricoParametrizacao,
} from '../models/response/ciclo.model';
import {ReservaCicloRequest, VagaCiclo, VagaCicloRequest} from '../models/response/vaga-ciclo.model';
import {PageModel} from '../models/pagination/page.model';

@Injectable({
  providedIn: 'root'
})
export class CicloService {
  private readonly apiUrl = `${environment.fullApiUrl}/ciclo`;
  private readonly http = inject(HttpClient);

  // Operações CRUD de Ciclo

  criar(ciclo: CicloRequest): Observable<CicloResponse> {
    return this.http.post<CicloResponse>(this.apiUrl, ciclo);
  }

  listarTodos(): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(this.apiUrl);
  }

  listarTodas(): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(`${this.apiUrl}/listar-todas`);
  }

  listarOpcoes(): Observable<CicloOpcaoResponse[]> {
    return this.http.get<CicloOpcaoResponse[]>(`${this.apiUrl}/opcoes`);
  }

  listarOpcoesMaisMedicos(): Observable<CicloOpcaoResponse[]> {
    return this.http.get<CicloOpcaoResponse[]>(`${this.apiUrl}/opcoes/mais-medicos`);
  }

  listarPorPrograma(programaId: number): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(`${this.apiUrl}/programa/${programaId}`);
  }

  listarPorAno(ano: number): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(`${this.apiUrl}/ano/${ano}`);
  }

  listarPorSituacao(situacaoId: number): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(`${this.apiUrl}/situacao/${situacaoId}`);
  }

  listarRascunhosPorPrograma(programaId: number): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(`${this.apiUrl}/programa/${programaId}/rascunhos`);
  }

  listarAtivosPorPrograma(programaId: number): Observable<CicloResponse[]> {
    return this.http.get<CicloResponse[]>(`${this.apiUrl}/programa/${programaId}/ativos`);
  }

  buscarPorId(id: number): Observable<CicloResponse> {
    return this.http.get<CicloResponse>(`${this.apiUrl}/${id}`);
  }

  existe(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/${id}/existe`);
  }

  estaFinalizado(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/${id}/finalizado`);
  }

  atualizar(id: number, ciclo: CicloRequest): Observable<CicloResponse> {
    return this.http.put<CicloResponse>(`${this.apiUrl}/${id}`, ciclo);
  }

  excluirLogica(id: number, justificativa: string): Observable<void> {
    const params = new HttpParams().set('justificativa', justificativa);
    return this.http.delete<void>(`${this.apiUrl}/${id}/excluir`, { params });
  }

  excluirFisica(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // Operações de Vagas

  vincularVagas(cicloId: number, request: VagaCicloRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${cicloId}/vagas`, request);
  }

  listarVagasVinculadas(cicloId: number): Observable<VagaCiclo[]> {
    return this.http.get<VagaCiclo[]>(`${this.apiUrl}/${cicloId}/vagas`);
  }

  removerVagas(cicloId: number, request: VagaCicloRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${cicloId}/vagas/remover`, request);
  }

  aplicarReservas(cicloId: number, request: ReservaCicloRequest): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${cicloId}/reservas`, request);
  }

  // Informações e Resumos

  obterResumo(cicloId: number): Observable<CicloResumo> {
    return this.http.get<CicloResumo>(`${this.apiUrl}/${cicloId}/resumo`);
  }

  obterInfo(cicloId: number): Observable<CicloInfo> {
    return this.http.get<CicloInfo>(`${this.apiUrl}/${cicloId}/info`);
  }

  // Busca paginada com filtros

  search(page: number, size: number, filtro?: CicloFiltro): Observable<PageModel<CicloResponse>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'id,desc');

    if (filtro) {
      const filterParts: string[] = [];

      if (filtro.etapaParametrizacaoId) {
        filterParts.push(`etapaParametrizacaoId==${filtro.etapaParametrizacaoId}`);
      }
      if (filtro.faseParametrizacaoId) {
        filterParts.push(`faseParametrizacaoId==${filtro.faseParametrizacaoId}`);
      }
      if (filtro.nomeCiclo) {
        filterParts.push(`nomeCiclo==${filtro.nomeCiclo}`);
      }
      if (filtro.vigente !== undefined && filtro.vigente !== null) {
        filterParts.push(`vigente==${filtro.vigente}`);
      }
      if (filtro.numeroCiclo) {
        filterParts.push(`numeroCiclo==${filtro.numeroCiclo}`);
      }
      if (filtro.anoCiclo) {
        filterParts.push(`anoCiclo==${filtro.anoCiclo}`);
      }
      if (filtro.tipoEditalId) {
        filterParts.push(`tipoEditalId==${filtro.tipoEditalId}`);
      }
      if (filtro.programaId) {
        filterParts.push(`programaId==${filtro.programaId}`);
      }
      if (filtro.situacaoId) {
        filterParts.push(`situacaoId==${filtro.situacaoId}`);
      }
      if (filtro.ativo !== undefined && filtro.ativo !== null) {
        filterParts.push(`ativo==${filtro.ativo}`);
      }
      if (filtro.loadInscricao !== undefined && filtro.loadInscricao !== null) {
        filterParts.push(`loadInscricao==${filtro.loadInscricao}`);
      }

      if (filterParts.length > 0) {
        params = params.set('filter', filterParts.join(';'));
      }
    }

    return this.http.get<PageModel<CicloResponse>>(`${this.apiUrl}/search`, { params });
  }

  // Histórico de alterações (RN009)

  /** PGPP-678 (RN009): histórico de todas as etapas da parametrização, com campo e valores anterior/novo. */
  buscarHistoricoParametrizacao(cicloId: number, page: number = 0, size: number = 10): Observable<PageModel<HistoricoParametrizacao>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<PageModel<HistoricoParametrizacao>>(`${this.apiUrl}/${cicloId}/historico-parametrizacao`, { params });
  }

  buscarHistorico(cicloId: number, page: number = 0, size: number = 10): Observable<PageModel<HistoricoCiclo>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'id.dtAcao,desc');
    return this.http.get<PageModel<HistoricoCiclo>>(`${this.apiUrl}/${cicloId}/historico`, { params });
  }

  // Obter ciclo anterior
  obterCicloAnterior(programaId: number, numeroCiclo: number, anoCiclo: number): Observable<CicloResponse | null> {
    // Buscar todos os ciclos do programa ordenados por ano e número
    return this.http.get<CicloResponse | null>(
      `${this.apiUrl}/programa/${programaId}/anterior`,
      {
        params: new HttpParams()
          .set('numeroCiclo', String(numeroCiclo))
          .set('anoCiclo', String(anoCiclo))
      }
    );
  }
}
